'use client';
import React, { useCallback, useEffect, useState } from 'react';
import type { BasicToastrOptions } from 'react-redux-toastr';
import { toastr } from 'react-redux-toastr';
import { useRouter } from 'next/navigation';

import { useReactiveVar } from '@nerdwallet/apollo';
import { EphemeralAlert } from '@nerdwallet/nerdwallet-plus-components';
import { Box } from '@nerdwallet/currency/components/Box';

import Paths from '@lib/paths';
import { RewardAlertConfigMap } from '@constants/nerdwallet-plus-rewards';
import SegmentImpression from '@components/segment-impression';
import { nwPlusEphemeralAlertQueueVar } from '@lib/apollo/nerdwallet-plus/reactive-vars';
import styles from './use-display-ephemeral-alert.module.scss';

const MOBILE_WEB_TOAST_TIMEOUT = 3000;
// Including additional time so that a user can view the toast
// after the bounce down animation has settled
const WEB_TOAST_TIMEOUT = 5000;
const MOBILE_TOAST_WIDTH = '270px';
const TOAST_WIDTH = '300px';

interface Props {
  isMobile: boolean;
}

/**
 * This hook listens for queued rewarded events for NerdWallet+ members and displays the alerts.
 */
const useDisplayEphemeralAlert = ({ isMobile }: Props): void => {
  const router = useRouter();
  const [toastIsShown, setToastIsShown] = useState(false);
  const ephemeralAlertQueue = useReactiveVar(nwPlusEphemeralAlertQueueVar);
  const [currentEphemeralAlert] = ephemeralAlertQueue;

  const handleRewardsDashboardClick = useCallback(() => {
    router.push(Paths.NERDWALLET_PLUS);
  }, [router]);

  useEffect(() => {
    // If in Mobile Web, we only want to show one alert at a time
    if (currentEphemeralAlert?.eventType && (!isMobile || !toastIsShown)) {
      const currentEventType = currentEphemeralAlert?.eventType;
      const currentAlertConfig = RewardAlertConfigMap[currentEventType];
      const toastrOptions: BasicToastrOptions = {
        timeOut: isMobile ? MOBILE_WEB_TOAST_TIMEOUT : WEB_TOAST_TIMEOUT,
        className: styles.ephemeralAlertToastr,
        onHideComplete: () => setToastIsShown(false),
        transitionIn: 'bounceInDown',
        transitionOut: 'fadeOut',
        component: (
          <SegmentImpression
            eventName="Rewards Points Earned Alert Viewed"
            eventProps={{
              reward_activity: currentAlertConfig.analyticType,
              points_earned: currentEphemeralAlert?.rewardPoints,
              alert_type: 'ephemeral',
            }}
          >
            <Box display="flex" justifyContent="center" width={100}>
              <EphemeralAlert
                points={currentEphemeralAlert?.rewardPoints}
                text={currentAlertConfig.alertText}
                animate
                isLarge={currentAlertConfig.isLarge}
                onClick={handleRewardsDashboardClick}
                width={isMobile ? MOBILE_TOAST_WIDTH : TOAST_WIDTH}
              />
            </Box>
          </SegmentImpression>
        ),
      };
      // Render custom component only
      toastr.success('', '', toastrOptions);
      setToastIsShown(true);
      const newEphemeralAlertQueue = new Set(ephemeralAlertQueue);
      newEphemeralAlertQueue.forEach((reward) => {
        if (reward.eventType === currentEventType) {
          newEphemeralAlertQueue.delete(reward);
        }
      });
      nwPlusEphemeralAlertQueueVar(newEphemeralAlertQueue);
    }
  }, [
    currentEphemeralAlert,
    ephemeralAlertQueue,
    handleRewardsDashboardClick,
    isMobile,
    toastIsShown,
  ]);
};

export default useDisplayEphemeralAlert;
